import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import WeddingSchedule from './WeddingSchedule';
import StaticMountains from './StaticMountains';
import HeroSection from './HeroSection';
import Footer from './Footer';
import { colors } from '../styles/colors';

const FullScreenContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  overflow-x: hidden;
  overscroll-behavior: none;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${colors.neutral.black};
`;

const SolidBackground = styled.div`
  width: 100%;
  min-height: 1000px;
  background-color: ${colors.neutral.darkBrown};
  background-size: 
    2px 2px, 3px 3px, 1px 1px, 4px 4px, 5px 5px, 6px 6px, 
    2px 2px, 7px 7px, 1px 1px, 8px 8px, 2px 2px, 3px 3px;
  background-position: 
    0 0, 1px 1px, 2px 2px, 3px 3px, 4px 4px, 5px 5px,
    6px 6px, 7px 7px, 8px 8px, 9px 9px, 10px 10px, 11px 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    min-height: 800px;
  }

  @media (max-width: 480px) {
    min-height: 600px;
  }
`;

const NameText = styled.div`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(1.5rem, 4vw, 3rem);
  color: ${colors.neutral.cream};
  line-height: 1.2;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: clamp(1.3rem, 3.5vw, 2.5rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(1.1rem, 3vw, 2rem);
  }
`;

const SeamlessTransition = styled.div`
  width: 100%;
  min-height: 500px;
  background-color: ${colors.primary.mediumBrown}; /* Match the front mountain color */
  margin-top: -10px; /* Small negative margin to eliminate gaps with mountain */
  position: relative;
  z-index: 70; /* Increased from 50 to ensure it's above the mountain (z-index 60) */
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* Change back to left alignment */
  justify-content: center;
  box-sizing: border-box;
  text-align: left; /* Change back to left */

  @media (max-width: 768px) {
    margin-top: -10px;
    padding: 80px 0px 30px 30px; /* Add left padding for tablets */
    min-height: 300px;
  }

  @media (max-width: 480px) {
    margin-top: -10px;
    padding: 70px 0px 25px 20px; /* Add left padding for mobile */
    min-height: 250px;
  }
`;

const ImagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 40px;

  @media (max-width: 768px) {
    margin-top: 30px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const VenueTitle = styled.div`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(2rem, 4.5vw, 3.5rem);
  color: ${colors.neutral.darkOlive};
  text-align: center;
  line-height: 1.3;
  letter-spacing: 1.5px;
  margin-top: 40px;
  margin-bottom: 0px;

  .subtitle {
    font-size: clamp(1.2rem, 2.5vw, 2rem);
    display: block;
    margin-top: 5px;
  }

  .location {
    font-size: clamp(1rem, 2vw, 1.5rem);
    display: block;
    margin-top: 5px;
    font-style: italic;
  }

  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 4vw, 3rem);
    margin-top: 30px;
    margin-bottom: 0px;

    .subtitle {
      font-size: clamp(1rem, 2.2vw, 1.8rem);
    }

    .location {
      font-size: clamp(0.9rem, 1.8vw, 1.3rem);
    }
  }

  @media (max-width: 480px) {
    font-size: clamp(1.5rem, 3.5vw, 2.5rem);
    margin-top: 25px;
    margin-bottom: 0px;

    .subtitle {
      font-size: clamp(0.9rem, 2vw, 1.5rem);
    }

    .location {
      font-size: clamp(0.8rem, 1.6vw, 1.2rem);
    }
  }
`;

const LeftImage = styled.img`
  max-height: 600px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  margin-top: -20px;

  @media (max-width: 768px) {
    max-height: 450px;
    margin-top: -15px;
  }

  @media (max-width: 480px) {
    max-height: 375px;
    margin-top: -10px;
  }
`;

const MountainFlipContainer = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  z-index: 25;
  margin-top: -150px;
  background-color: ${colors.primary.lightTan}; /* Match the new light background */

  /* Shrink the mountains section on tablets */
  @media (max-width: 768px) {
    height: 420px;
    margin-top: -120px;
  }

  /* Further shrink on phones */
  @media (max-width: 480px) {
    height: 320px;
    margin-top: -100px;
  }
`;

const MountainFlipImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center bottom;
  display: block;
  background-color: ${colors.primary.lightTan};

  @media (max-width: 768px) {
    object-position: center bottom;
  }

  @media (max-width: 480px) {
    object-position: center bottom;
  }
`;

const PhotoNamesContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  padding: 0 40px;
  margin-top: clamp(-700px, -44vw, -900px);
  position: relative;
  z-index: 30;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    margin-top: clamp(-510px, -39vw, -660px);
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    margin-top: clamp(-410px, -34vw, -510px);
    padding: 0 15px;
    gap: 15px;
  }
`;

const LeftName = styled.div`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(1.5rem, 4vw, 3rem);
  color: ${colors.neutral.cream};
  line-height: 1.2;
  letter-spacing: 1px;
  text-align: left;
  margin-right: 20px;

  @media (max-width: 768px) {
    font-size: clamp(1.3rem, 3.5vw, 2.5rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(1.1rem, 3vw, 2rem);
  }
`;

const RightName = styled.div`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(1.5rem, 4vw, 3rem);
  color: ${colors.neutral.cream};
  line-height: 1.2;
  letter-spacing: 1px;
  text-align: right;
  margin-left: 20px;

  @media (max-width: 768px) {
    font-size: clamp(1.3rem, 3.5vw, 2.5rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(1.1rem, 3vw, 2rem);
  }
`;

const DateText = styled.div`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  color: ${colors.neutral.darkOlive};
  text-align: center;
  line-height: 1.2;
  letter-spacing: 2px;
  margin-top: 20px;

  @media (max-width: 768px) {
    font-size: clamp(1.5rem, 3vw, 2.3rem);
    margin-top: 15px;
  }

  @media (max-width: 480px) {
    font-size: clamp(1.2rem, 2.5vw, 1.8rem);
    margin-top: 10px;
  }
`;

const RSVPSection = styled.section`
  width: 100%;
  min-height: 200px;
  background-color: ${colors.primary.lightTan};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 20px;
  box-sizing: border-box;
  position: relative;
  z-index: 30;

  @media (max-width: 768px) {
    min-height: 180px;
    padding: 12px 15px;
  }

  @media (max-width: 480px) {
    min-height: 160px;
    padding: 10px 10px;
  }
`;

const RSVPTitle = styled.h2`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: ${colors.neutral.darkOlive};
  text-align: center;
  margin: 0 0 20px 0;
  letter-spacing: 2px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: clamp(2.5rem, 5vw, 3.8rem);
    margin: 0 0 15px 0;
  }

  @media (max-width: 480px) {
    font-size: clamp(2.3rem, 5.5vw, 3.5rem);
    margin: 0 0 10px 0;
  }
`;

const RSVPButton = styled.button`
  font-family: 'GremeryOne', cursive;
  font-size: clamp(2rem, 4vw, 3rem);
  color: ${colors.neutral.darkBrown};
  background: none;
  border: none;
  padding: 15px 40px;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    padding: 12px 35px;
    font-size: clamp(2rem, 4.5vw, 2.8rem);
  }

  @media (max-width: 480px) {
    padding: 10px 30px;
    font-size: clamp(2rem, 5vw, 3rem);
  }
`;

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  // Fix for mobile Safari viewport height
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  const handleRSVPClick = () => {
    navigate('/rsvp');
    window.scrollTo(0, 0);
  };
  const handleDetailsClick = () => {
    navigate('/travel');
    window.scrollTo(0, 0);
  };

  return (
    <FullScreenContainer>
      <HeroSection />
      {/* <SolidBackground>
        <PhotoNamesContainer>
        </PhotoNamesContainer>
      </SolidBackground> */}
      <StaticMountains />
      <SeamlessTransition id="details">
        <ImagesContainer>
          <VenueTitle>
            The Four Seasons<br />
            <span className="subtitle">Cabo Del Sol</span>
            <span className="location">Cabo San Lucas, MX</span>
          </VenueTitle>
          <LeftImage src="/images/four-seas.png" alt="Four Seasons" />
          <DateText>12.05.26</DateText>
        </ImagesContainer>
      </SeamlessTransition>
      <MountainFlipContainer>
        <MountainFlipImage src="/images/mountains/mountain-3-flip.svg" alt="Mountain landscape" />
      </MountainFlipContainer>
      <WeddingSchedule />
      <RSVPSection>
        <RSVPTitle>See you there</RSVPTitle>
        {/* <RSVPButton onClick={handleRSVPClick}>
          RSVP
        </RSVPButton> */}
        <RSVPButton onClick={handleDetailsClick}>
          DETAILS
        </RSVPButton>
      </RSVPSection>
      <Footer />
    </FullScreenContainer>
  );
};

export default HomePage;
